"use client";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from '../common/Toast/toast';
import { useAuth } from "../../../context/AuthContext";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import "../Trainer/TrainerDashboard.css";
import "../common/lms-pages.css";
import { getAllStudents, setStudentStatus } from "../../../services/StudentFromBillingService.js";
import { confirm } from "../common/Confirm/confirm";
import { initialSearchFromUrl } from '../../../utils/urlSearch';
import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import '../common/waypoint/waypoint.css';
import FilterSheet from '../common/FilterSheet/FilterSheet';
import useDebouncedCallback from '../common/useDebouncedCallback';

const StudentFromBilling = () => {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();

  // State for data and pagination
  const [studentData, setStudentData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  // Date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [isDateFilterApplied, setIsDateFilterApplied] = useState(false);

  // Search states
  // Seeded from ?search= so a hit in the topbar search opens this list
  // already filtered to the row that was clicked.
  const [searchTerm, setSearchTerm] = useState(initialSearchFromUrl);
  const [selectedField, setSelectedField] = useState("all");
  const [details, setDetails] = useState(null);
  // Which tab the student popup is showing. Reset per student, not per
  // render, so opening someone new always starts on Details.
  const [detailsTab, setDetailsTab] = useState("details");
  // The nav bridge, for drilling out of this page into other entities.
  const { open: openEntity, push: pushEntity } = useWaypoint();
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState(initialSearchFromUrl);

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);

  // Refs for preventing duplicate calls and caching
  const isFetching = useRef(false);
  const lastFetchRef = useRef("");
  const serverCacheRef = useRef({});

  const hasShownToast = useRef(false);

  // Permission check
  useEffect(() => {
    if (!hasPermission("all-students", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to list students.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Fetch students with server-side pagination
  const fetchStudents = useCallback(async () => {
    const fetchKey = `${recordsPerPage}-${currentPage}-${searchTerm}-${selectedField}-${startDate}-${endDate}`;

    if (lastFetchRef.current === fetchKey) return;
    if (isFetching.current) return;

    // ✅ Check cache first
    if (serverCacheRef.current[fetchKey] && !appendRef.current) {
      const cached = serverCacheRef.current[fetchKey];
      setStudentData(cached.data);
      setTotalRecords(cached.total);
      lastFetchRef.current = fetchKey;
      return;
    }

    isFetching.current = true;
    lastFetchRef.current = fetchKey;

    const isAppend = appendRef.current;

    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);

      const response = await getAllStudents(
        currentPage,
        recordsPerPage,
        searchTerm,
        selectedField,
        startDate,
        endDate,
      );

      const students = response.data || [];
      const pagination = response.pagination || {};

      // ✅ Store in cache
      serverCacheRef.current[fetchKey] = {
        data: students,
        total: pagination.total || 0,
      };

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setStudentData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...students.filter((r) => !seen.has(r.id))];
        });
      } else {
        setStudentData(students);
      }
      appendRef.current = false;
      setTotalRecords(pagination.total || 0);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to fetch student data.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      isFetching.current = false;
    }
  }, [
    recordsPerPage,
    currentPage,
    searchTerm,
    startDate,
    endDate,
  ]);

  // Fetch students when dependencies change
  useEffect(() => {
    if (user?.email) {
      fetchStudents();
    }
  }, [fetchStudents, user]);

  // Apply date filter
  const applyDateFilter = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setIsDateFilterApplied(true);
    setCurrentPage(1);
    serverCacheRef.current = {}; // ✅ clear cache on new date filter
    lastFetchRef.current = "";
  };

  // Clear date filter
  const clearDateFilter = () => {
    setTempStartDate("");
    setTempEndDate("");
    setStartDate("");
    setEndDate("");
    setIsDateFilterApplied(false);
    setCurrentPage(1);
    serverCacheRef.current = {}; // ✅ clear cache when filter removed
    lastFetchRef.current = "";
  };

  const hasMore = studentData.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, studentData.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSelectedField(field);
    // ✅ clear cache on new search
    serverCacheRef.current = {};
    lastFetchRef.current = "";
    runSearch(value);
  };

  // ✅ Helper: extract names from array of objects
  const extractNames = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return "N/A";
    return arr.map((item) => item.name || "N/A").join(", ");
  };

  // The batches a student is on, as rows rather than one comma-joined
  // string. A student on four batches used to render as four names run
  // together in a single <dd>, which said nothing about when any of them
  // meets -- the thing you open a student to find out.
  /* Memoised because studentCourses below depends on it: a fresh array
     every render would make that memo recompute every render, which is
     the same as not having one. */
  const studentBatches = useMemo(
    () => (Array.isArray(details?.batchIds) ? details.batchIds : []),
    [details],
  );

  /* The courses the student is enrolled on, each with the batches of
     theirs that teach it.

     The Details tab already lists the names, comma-joined in one <dd> --
     which answers "which courses" and nothing else. Enrolled on three
     courses, that line cannot say which of them is actually running, and
     that is the question someone opens a student to ask.

     The link is batch.courseId, which arrives either as a joined object
     or as a bare id depending on the route that served it, so both are
     read. A course with no batch is not an error -- it is a course paid
     for and not yet scheduled, which is worth seeing plainly. */
  const courseKey = (value) => {
    if (value == null) return '';
    if (typeof value === 'object') return String(value.id ?? value._id ?? '').trim();
    return String(value).trim();
  };

  const studentCourses = useMemo(() => {
    const courses = Array.isArray(details?.courseIds) ? details.courseIds : [];
    return courses.map((course) => {
      const id = courseKey(course);
      const name = course?.name || 'Unnamed course';
      const batches = studentBatches.filter((b) => id && courseKey(b?.courseId) === id);
      return { id: id || name, name, batches };
    });
  }, [details, studentBatches]);

  // "monday,tuesday,thursday" -> "Mon, Tue, Thu". Left as-is if the stored
  // value is not the CSV the batch collection normally holds.
  const formatDays = (days) => {
    const list = String(days || "")
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
    if (list.length === 0) return "—";
    return list
      .map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3).toLowerCase())
      .join(", ");
  };

  // Start and end as one span, because a batch is a range and two columns
  // of dates side by side read as two unrelated facts.
  const formatSpan = (start, end) => {
    const s = start ? moment(start) : null;
    const e = end ? moment(end) : null;
    const sOk = s && s.isValid();
    const eOk = e && e.isValid();
    if (!sOk && !eOk) return "—";
    if (sOk && eOk) return `${s.format("DD MMM YYYY")} – ${e.format("DD MMM YYYY")}`;
    return (sOk ? s : e).format("DD MMM YYYY");
  };

  /* ── Active / inactive toggle ──────────────────────────────────────
     Inactive is not a label: the API locks an inactive student out of the
     app (studentaccess.service). So switching one OFF asks first; switching
     back ON does not. The row flips at once and flips back if the API says
     no -- only an admin, superadmin or CRM may do this, and the API's own
     message is what the toast shows either way. */
  const [statusPending, setStatusPending] = useState(() => new Set());
  const isActiveStudent = (row) => String(row?.status || "active").toLowerCase() !== "inactive";

  const setRowStatus = (id, status) =>
    setStudentData((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));

  const toggleStudentStatus = async (row) => {
    if (!row?.id || statusPending.has(row.id)) return;
    const next = isActiveStudent(row) ? "inactive" : "active";

    if (next === "inactive") {
      const answer = await confirm({
        title: `Make ${row.name || "this student"} inactive?`,
        text: "They will be signed out and cannot use the app until they are made active again.",
        confirmButtonText: "Make inactive",
        tone: "danger",
      });
      if (!answer.isConfirmed) return;
    }

    const previous = row.status || "active";
    setStatusPending((prev) => new Set(prev).add(row.id));
    setRowStatus(row.id, next);
    try {
      const { message } = await setStudentStatus(row.id, next);
      // The cached pages hold the old status; the next fetch must not.
      serverCacheRef.current = {};
      toast.success(message || `Student is now ${next}`);
    } catch (error) {
      setRowStatus(row.id, previous);
      toast.error(error?.message || "The status could not be changed");
    } finally {
      setStatusPending((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(row.id);
        return nextSet;
      });
    }
  };

  const headers = [
    {
      title: "Student",
      prop: "name",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.name || "Unnamed"}</span>
          {row.email && <span className="lp-sub">{row.email}</span>}
        </div>
      ),
    },
    {
      title: "Phone",
      prop: "phone",
      cell: (row) => <span className="lp-plain">{row.phone || "—"}</span>,
    },
    {
      title: "Course",
      prop: "courseName",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-plain">{extractNames(row.courseIds)}</span>
          <span className="lp-sub">{extractNames(row.preferredBranchId)}</span>
        </div>
      ),
    },
    {
      title: "Contact person",
      prop: "contactPersonName",
      cell: (row) => (
        <span className="lp-plain">
          {extractNames(row.contactPersonIds)}
        </span>
      ),
    },
    {
      title: "Verified",
      prop: "isVerifiedbyCounsellor",
      sortable: true,
      cell: (row) => (
        <span
          className={`pg-pill ${
            row.isVerifiedbyCounsellor ? "pg-pill--good" : "pg-pill--warn"
          }`}
        >
          {row.isVerifiedbyCounsellor ? "Verified" : "Unverified"}
        </span>
      ),
    },
    {
      title: "Status",
      prop: "status",
      cell: (row) => {
        const active = isActiveStudent(row);
        const pending = statusPending.has(row.id);
        return (
          <label
            className={`att-switch att-switch--status${active ? " is-on" : ""}${pending ? " is-busy" : ""}`}
            // The row opens the student on click; the toggle must not.
            onClick={(e) => e.stopPropagation()}
            title={active ? "Active — click to make inactive" : "Inactive — click to make active"}
          >
            <input
              type="checkbox"
              role="switch"
              checked={active}
              disabled={pending}
              aria-label={`${row.name || "Student"} is ${active ? "active" : "inactive"}`}
              onChange={() => toggleStudentStatus(row)}
            />
            <span className="att-switch-track" aria-hidden="true">
              <span className="att-switch-knob" />
            </span>
            <span className="att-switch-label">{active ? "Active" : "Inactive"}</span>
          </label>
        );
      },
    },
  ];

  const paginatedData = studentData.map((item, index) => ({
    ...item,
    srno: (currentPage - 1) * recordsPerPage + index + 1,
  }));

  if (!user?.email) {
    return (
      <div>
        <div>
          <h5>Authentication Required</h5>
          <p>Please log in to view students.</p>
        </div>
      </div>
    );
  }

  const canSeeFees = hasPermission("all-students", "view", "lms");

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          <span className="lp-count">
            {totalRecords} {totalRecords === 1 ? "student" : "students"}
          </span>
        </div>
      </header>

      {/* Everything that narrows the list, search included -- the table's
          own toolbar is switched off below so the two are not stacked one
          above the other. */}
      <FilterSheet label="Filter students">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-stu-search">
              Search
            </label>
            <input
              id="lp-stu-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by name, email, phone, course or branch..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, selectedField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-stu-field">
              Search in
            </label>
            <select
              id="lp-stu-field"
              className="pg-select"
              value={selectedField}
              onChange={(e) => onSearchChange(uiSearchTerm, e.target.value)}
            >
              <option value="all">All fields</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="contactPersonName">Contact person</option>
              <option value="branchName">Preferred branch</option>
              <option value="courseName">Course</option>
              <option value="departmentName">Department</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-stu-rows">
              Rows per load
            </label>
            <select
              id="lp-stu-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            <div className="pg-field">
              <label className="pg-label" htmlFor="lp-stu-from">
                Enrolled from
              </label>
              <input
                id="lp-stu-from"
                type="date"
                className="pg-input"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="lp-stu-to">
                Enrolled to
              </label>
              <input
                id="lp-stu-to"
                type="date"
                className="pg-input"
                min={tempStartDate || undefined}
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={applyDateFilter}
              disabled={!tempStartDate && !tempEndDate}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={clearDateFilter}
              disabled={!isDateFilterApplied}
            >
              Clear
            </button>
          </div>
        </div>

        {isDateFilterApplied && (startDate || endDate) && (
          <p className="lp-applied">
            Showing
            {startDate && ` from ${moment(startDate).format("DD MMM YYYY")}`}
            {endDate && ` to ${moment(endDate).format("DD MMM YYYY")}`}
          </p>
        )}
      </FilterSheet>

      <div className="td-list td-list--lms td-list--students">
        <NewCustomDataTable
          data={paginatedData}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => {
            setDetails(row);
            setDetailsTab("details");
          }}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more students...</span>
          </>
        ) : hasMore ? (
          <span>
            {studentData.length} of {totalRecords} students
          </span>
        ) : totalRecords > 0 ? (
          <span>
            All {totalRecords} student{totalRecords === 1 ? "" : "s"} loaded
          </span>
        ) : (
          !loading && <span>No students match those filters.</span>
        )}
      </div>

      {/* One student, in full. Fees, contact and enrolment live here
          rather than in eleven columns nobody can scan. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-stu-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Student</span>
                <h3 className="pg-modal-title" id="lp-stu-title">
                  {details.name || "Unnamed"}
                </h3>
                {details.studentIdNumber && (
                  <p className="pg-modal-sub">{details.studentIdNumber}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="lp-tabs" role="tablist" aria-label="Student">
              {[
                { key: "details", label: "Details" },
                { key: "courses", label: "Courses", n: studentCourses.length },
                { key: "batches", label: "Batches", n: studentBatches.length },
              ].map((t) => (
                <button
                  type="button"
                  key={t.key}
                  role="tab"
                  id={`lp-stu-tab-${t.key}`}
                  aria-selected={detailsTab === t.key}
                  aria-controls={`lp-stu-panel-${t.key}`}
                  className={`lp-tab${detailsTab === t.key ? " is-on" : ""}`}
                  onClick={() => setDetailsTab(t.key)}
                >
                  {t.label}
                  {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
                </button>
              ))}
            </div>

            <div className="pg-modal-body">
              {detailsTab === "details" && (
              <div
                role="tabpanel"
                id="lp-stu-panel-details"
                aria-labelledby="lp-stu-tab-details"
              >
              <dl className="quiz-facts">
                <div>
                  <dt>Verified</dt>
                  <dd>
                    <span
                      className={`pg-pill ${
                        details.isVerifiedbyCounsellor
                          ? "pg-pill--good"
                          : "pg-pill--warn"
                      }`}
                    >
                      {details.isVerifiedbyCounsellor
                        ? "Verified"
                        : "Unverified"}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{details.email || "—"}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{details.phone || "—"}</dd>
                </div>
                <div>
                  <dt>Course</dt>
                  <dd>{extractNames(details.courseIds)}</dd>
                </div>
                <div>
                  <dt>Department</dt>
                  <dd>{extractNames(details.departmentIds)}</dd>
                </div>
                <div>
                  <dt>Preferred branch</dt>
                  <dd>{extractNames(details.preferredBranchId)}</dd>
                </div>
                {/* Batches used to sit here as a comma-joined <dd>. It has
                    its own tab now, which is the only place with room to
                    say when each one actually runs. */}
                <div>
                  <dt>Mode of class</dt>
                  <dd>{details.modeOfClass || "—"}</dd>
                </div>
                <div>
                  <dt>Contact person</dt>
                  <dd>{extractNames(details.contactPersonIds)}</dd>
                </div>
                <div>
                  <dt>Added</dt>
                  <dd>
                    {details.createdAt
                      ? moment(details.createdAt).format("DD MMM YYYY, hh:mm A")
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>
                    {details.updatedAt
                      ? moment(details.updatedAt).format("DD MMM YYYY, hh:mm A")
                      : "—"}
                  </dd>
                </div>
              </dl>

              {canSeeFees && (
                <>
                  <h5 className="pgm-section-title">Fees</h5>
                  <dl className="quiz-facts">
                    <div>
                      <dt>Total</dt>
                      <dd>{details.totalFees ?? "—"}</dd>
                    </div>
                    <div>
                      <dt>Paid</dt>
                      <dd>{details.paidAmount ?? "—"}</dd>
                    </div>
                    <div>
                      <dt>Remaining</dt>
                      <dd>{details.remainingFees ?? "—"}</dd>
                    </div>
                    <div>
                      <dt>Due date</dt>
                      <dd>
                        {details.feesDueDate
                          ? moment(details.feesDueDate).format("DD MMM YYYY")
                          : "—"}
                      </dd>
                    </div>
                  </dl>
                </>
              )}
              </div>
              )}

              {detailsTab === "courses" && (
                <div
                  role="tabpanel"
                  id="lp-stu-panel-courses"
                  aria-labelledby="lp-stu-tab-courses"
                >
                  {studentCourses.length > 0 ? (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Course</th>
                            <th>Batches</th>
                            <th>Runs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentCourses.map((c, i) => (
                            <tr key={c.id}>
                              <td className="lp-num">{i + 1}</td>
                              <td className="lp-name">{c.name}</td>
                              <td>
                                {c.batches.length > 0 ? (
                                  <span className="lp-list-inline">
                                    {c.batches.map((b, bi) => (
                                      <span key={b?.id || `${c.id}-${bi}`}>
                                        {b?.name || 'Unnamed batch'}
                                      </span>
                                    ))}
                                  </span>
                                ) : (
                                  /* Paid for, not yet scheduled. Said in
                                     words rather than left as a dash,
                                     because a blank cell reads as missing
                                     data rather than as a fact. */
                                  <span className="lp-muted">Not scheduled yet</span>
                                )}
                              </td>
                              <td>
                                {c.batches.length > 0 ? (
                                  <span className="lp-list-inline">
                                    {c.batches.map((b, bi) => (
                                      <span key={b?.id || `${c.id}-span-${bi}`}>
                                        {formatSpan(b?.startTime, b?.endTime)}
                                      </span>
                                    ))}
                                  </span>
                                ) : (
                                  <span className="lp-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="lp-muted">
                      This student is not enrolled on any course yet.
                    </p>
                  )}
                </div>
              )}

              {detailsTab === "batches" && (
                <div
                  role="tabpanel"
                  id="lp-stu-panel-batches"
                  aria-labelledby="lp-stu-tab-batches"
                >
                  {studentBatches.length > 0 ? (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Batch</th>
                            <th>Mode</th>
                            <th>Runs</th>
                            <th>Days</th>
                            <th>Meeting</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {studentBatches.map((b, i) => (
                            <tr key={b?.id || `${b?.name}-${i}`}>
                              <td className="lp-num">{i + 1}</td>
                              <td className="lp-name">{b?.name || "Unnamed batch"}</td>
                              <td>{b?.mode || "—"}</td>
                              <td>{formatSpan(b?.startTime, b?.endTime)}</td>
                              <td>{formatDays(b?.days)}</td>
                              <td>
                                {b?.meetingLink ? (
                                  <a
                                    href={b.meetingLink}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                  >
                                    Join
                                  </a>
                                ) : (
                                  <span className="lp-muted">—</span>
                                )}
                              </td>
                              <td className="wp-rowact">
                                {/* Hands off to the bridge. This page does
                                    not know what a batch popup looks like
                                    and does not need to -- it knows an id
                                    and a type. The student's own popup is
                                    closed first so the two are not stacked;
                                    the bridge's back link returns here. */}
                                {b?.id && (
                                  <button
                                    type="button"
                                    className="wp-link"
                                    onClick={() => {
                                      const studentId = details?.id;
                                      setDetails(null);
                                      if (studentId) {
                                        openEntity("student", studentId, { tab: "batches" });
                                        pushEntity("batch", b.id);
                                      } else {
                                        openEntity("batch", b.id);
                                      }
                                    }}
                                  >
                                    Open
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="lp-muted">
                      This student is not on any batch yet.
                    </p>
                  )}
                </div>
              )}
            </div>

            <footer className="pg-modal-foot">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentFromBilling;
